import React, { createContext, useContext, useEffect, useState } from "react";
var Context = createContext(false);
/**
 * Why does this exist?
 * We want to do the initial render on the client with the data that was used on
 * the server. This way we avoid any hydration mismatch errors. Usually we just
 * render on the server with the actual data. But some components can't be
 * rendered on the server. (e.g. login button) So for these clientside only
 * component, we usually render nothing or a loading state. Then on the client
 * we want to render again with this data. Components don't really have any
 * knowledge about what was rendered on the server. They are just pure
 * functions. So we want know when hydration is done so we can switch to real
 * data for these components.
 *
 * How do we know when hydration is done?
 * Hydration is basically the first clientside render. Effects using the
 * useEffect hooks are run after the component renders. So if we take our
 * top-level component, we know when hydrating is done when that effect runs.
 * UseEffect effects are not run on the server. We also want this effect to only
 * run once, because we are just interested in the transition from false to
 * true. Nothing else.
 *
 * So we just start out with hydrateComplete = false. We put this in context so
 * other components can read this value. Then we execute an effect in the Root
 * component and update hydrateComplete = true. Now other components get updated
 * with this new value and can start rendering with real data.
 */
export var Provider = function (_a) {
    var children = _a.children, force = _a.force;
    var _b = useState(force !== undefined ? force : false), hydrateComplete = _b[0], setHydrateComplete = _b[1];
    useEffect(function () {
        setHydrateComplete(true);
    }, []);
    return (React.createElement(Context.Provider, { value: hydrateComplete }, children));
};
/**
 * Why use context? Why not just use a component level effect?
 * When using the application clientside, components get destroyed and
 * created. So if we just use an effect, every component using this hook will
 * do an extra render, even though they already have access to the real data.
 * When we use context, once hydration is done, we can just do normal rendering.
 */
export var useComplete = function () { return useContext(Context); };
